import { Fragment, useState, useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { Button } from '../ui/button';
import { ScrollArea } from '../ui/scroll-area';
import { Separator } from '../ui/separator';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  LayoutDashboard,
  FolderOpen,
  Users,
  Settings,
  Moon,
  Sun,
  LogOut,
  ChevronDown,
  Plus,
  Building2,
  User,
  Search,
  Command,
  BarChart3,
  Sparkles,
  Shield,
  CreditCard,
  Layers,
  MessagesSquare,
  Headset,
  SlidersHorizontal,
  Bot,
  Database,
  MessageSquare,
  Mic,
  FileText,
  Lightbulb,
  Smartphone,
  Play,
  History,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import SearchModal from '../search/SearchModal';
import AuthBrandLogo from '../AuthBrandLogo';
import SidebarPlanUsage from '../plan/SidebarPlanUsage';
import { usePlanContext } from '../../hooks/usePlanContext';

const navItems = [
  { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard' },
  { icon: FolderOpen, label: 'Spaces', path: '/dashboard/spaces' },
  { icon: BarChart3, label: 'Insights', path: '/dashboard/insights' },
  { icon: MessagesSquare, label: 'FAQs', path: '/dashboard/faqs' },
  { icon: Users, label: 'Members', path: '/dashboard/members' },
  { icon: CreditCard, label: 'Pricing', path: '/dashboard/pricing' },
  { icon: Settings, label: 'Settings', path: '/dashboard/settings' },
  { icon: Headset, label: 'Help Center', path: '/dashboard/help' },
];

// The AI Agent group renders immediately after this nav entry.
const AGENT_GROUP_ANCHOR = '/dashboard/members';

const agentNavItems = [
  { icon: Bot, label: 'Agents', path: '/dashboard/agent/agents' },
  { icon: LayoutDashboard, label: 'AI Agent Dashboard', path: '/dashboard/agent/dashboard' },
  { icon: Database, label: 'Knowledge Source Mapping', path: '/dashboard/agent/knowledge-sources' },
  { icon: MessageSquare, label: 'Custom Prompt & Persona', path: '/dashboard/agent/prompt-persona' },
  { icon: Mic, label: 'Voice Selection', path: '/dashboard/agent/voice-selection' },
  { icon: FileText, label: 'Central Chat Logs', path: '/dashboard/agent/chat-logs' },
  { icon: Lightbulb, label: 'Knowledge Gap Insights', path: '/dashboard/agent/knowledge-gaps' },
  { icon: Smartphone, label: 'Mobile Widget Default', path: '/dashboard/agent/widget-default' },
  { icon: Play, label: 'Mobile Widget Active State', path: '/dashboard/agent/widget-active' },
  { icon: History, label: 'Chat History Drawer', path: '/dashboard/agent/chat-history' },
];

const AgentNavGroup = () => {
  const location = useLocation();
  const isInside = location.pathname.startsWith('/dashboard/agent');
  const [expanded, setExpanded] = useState(isInside);

  // Opening an agent page from elsewhere (e.g. a dashboard link) reveals the group.
  useEffect(() => {
    if (isInside) setExpanded(true);
  }, [isInside]);

  return (
    <div className="pt-1">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className={cn(
          'w-full flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-md transition-colors',
          isInside
            ? 'text-blue-700 dark:text-blue-400'
            : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-white'
        )}
        aria-expanded={expanded}
        data-testid="nav-ai-agent-group"
      >
        <Sparkles className="w-5 h-5 shrink-0" />
        <span className="flex-1 text-left truncate">AI Agent</span>
        <span className="text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
          Beta
        </span>
        <ChevronDown
          className={cn('w-4 h-4 shrink-0 transition-transform', !expanded && '-rotate-90')}
        />
      </button>

      {expanded && (
        <div className="mt-0.5 space-y-0.5 border-l border-slate-200 dark:border-slate-800 ml-5 pl-2">
          {agentNavItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-2.5 px-2.5 py-1.5 text-[13px] font-medium rounded-md transition-colors',
                  isActive
                    ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400'
                    : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-white'
                )
              }
              data-testid={`nav-agent-${item.path.split('/').pop()}`}
            >
              <item.icon className="w-4 h-4 shrink-0" />
              <span className="flex-1 truncate">{item.label}</span>
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
};

const superAdminNavItems = [
  { icon: LayoutDashboard, label: 'Overview', path: '/dashboard/super-admin/overview' },
  { icon: Building2, label: 'Organizations', path: '/dashboard/super-admin/organizations' },
  { icon: Users, label: 'Users', path: '/dashboard/super-admin/users' },
  { icon: Layers, label: 'Plans', path: '/dashboard/super-admin/plans' },
  { icon: Headset, label: 'Help Center', path: '/dashboard/super-admin/support' },
  { icon: Sparkles, label: 'Platform', path: '/dashboard/super-admin/platform' },
  { icon: SlidersHorizontal, label: 'Platform Control', path: '/dashboard/super-admin/platform-control' },
];

const Sidebar = ({ organizations = [], onCreateOrg }) => {
  const {
    user,
    currentOrg,
    selectOrganization,
    logout,
    superAdminViewMode,
    setSuperAdminMode,
  } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [showSearch, setShowSearch] = useState(false);
  const { context: planContext, loading: planLoading } = usePlanContext(currentOrg?.id);
  const searchEnabled =
    !planLoading &&
    (planContext?.addons?.search === true || planContext?.features?.algolia_search === true);
  const isSuperAdmin = user?.platform_role === 'super_admin';
  const isSuperAdminView = isSuperAdmin && superAdminViewMode === 'super_admin';
  const handleSuperAdminViewToggle = () => {
    if (isSuperAdminView) {
      setSuperAdminMode('company');
      navigate('/dashboard');
    } else {
      setSuperAdminMode('super_admin');
      navigate('/dashboard/super-admin/overview');
    }
  };

  // Keyboard shortcut for search (Cmd/Ctrl + K)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (isSuperAdmin && superAdminViewMode === 'super_admin') return;
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setShowSearch(true);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isSuperAdmin, superAdminViewMode]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleSelectOrg = (org) => {
    selectOrganization(org);
    navigate('/dashboard');
  };

  return (
    <aside
      className="fixed left-0 top-0 h-full w-64 bg-slate-50 dark:bg-slate-950 border-r border-slate-200 dark:border-slate-800 flex flex-col z-40"
      data-testid="sidebar"
    >
      {/* Logo */}
      <div className="flex items-center px-4 h-16 border-b border-slate-200 dark:border-slate-800" style={{ justifyContent: 'center' }}>
        <AuthBrandLogo className="h-8 w-auto max-w-[180px] object-contain object-left" />
      </div>

      {isSuperAdmin && (
        <div className="px-3 pt-3">
          <Button
            variant="outline"
            className="w-full justify-between bg-white dark:bg-slate-900 border-violet-200 dark:border-violet-800 text-slate-900 dark:text-white"
            onClick={handleSuperAdminViewToggle}
            data-testid="super-admin-view-toggle"
          >
            <span className="text-sm">
              {isSuperAdminView ? 'Switch to company view' : 'Switch to super admin view'}
            </span>
            <Shield className="w-4 h-4 text-violet-600 dark:text-violet-400" />
          </Button>
        </div>
      )}

      {!isSuperAdminView && (
        <div className="px-3 py-3">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                className="w-full justify-between bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white"
                data-testid="org-switcher"
              >
                <div className="flex items-center gap-2 truncate">
                  <Building2 className="w-4 h-4 text-slate-500" />
                  <span className="truncate">
                    {currentOrg ? currentOrg.name : 'Select Workspace'}
                  </span>
                </div>
                <ChevronDown className="w-4 h-4 text-slate-500" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-56" align="start">
              <DropdownMenuLabel>Workspaces</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {organizations.length > 0 ? (
                organizations.map((org) => (
                  <DropdownMenuItem
                    key={org.id}
                    onClick={() => handleSelectOrg(org)}
                    data-testid={`org-item-${org.id}`}
                  >
                    <Building2 className="w-4 h-4 mr-2" />
                    <span className="truncate">{org.name}</span>
                    {currentOrg?.id === org.id && (
                      <span className="ml-auto text-blue-600 dark:text-blue-400">●</span>
                    )}
                  </DropdownMenuItem>
                ))
              ) : (
                <div className="px-2 py-4 text-sm text-slate-500 text-center">
                  No workspaces yet
                </div>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={onCreateOrg} data-testid="create-org-button">
                <Plus className="w-4 h-4 mr-2" />
                Create Workspace
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}

      <Separator className="bg-slate-200 dark:bg-slate-800" />

      {!isSuperAdminView && (
        <div className="px-3 py-2">
          <Button
            variant="outline"
            className={`w-full justify-start ${
              searchEnabled
                ? 'text-slate-500 hover:text-slate-900 dark:hover:text-white'
                : 'text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'
            }`}
            onClick={() => setShowSearch(true)}
            data-testid="search-button"
            title={
              searchEnabled
                ? 'Search documentation (Ctrl+K)'
                : 'Advanced Search add-on required — click to learn more'
            }
          >
            <Search className="w-4 h-4 mr-2 shrink-0" />
            <span className="flex-1 text-left truncate">
              {searchEnabled ? 'Search...' : 'Search (add-on)'}
            </span>
            <kbd className="hidden sm:inline-flex items-center gap-1 px-1.5 py-0.5 text-xs bg-slate-100 dark:bg-slate-800 rounded shrink-0">
              <Command className="w-3 h-3" />K
            </kbd>
          </Button>
        </div>
      )}

      {/* Navigation + sticky attribution (flex-1 + min-h-0 keeps footer on screen) */}
      <div className="flex min-h-0 flex-1 flex-col">
        <ScrollArea className="min-h-0 flex-1 px-3 py-4">
          <nav className="space-y-1">
          {!isSuperAdminView && navItems.map((item) => (
              <Fragment key={item.path}>
                <NavLink
                  to={item.path}
                  end={item.path === '/dashboard'}
                  className={({ isActive }) =>
                    cn(
                      'flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-md transition-colors',
                      isActive
                        ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400'
                        : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-white'
                    )
                  }
                  data-testid={`nav-${item.label.toLowerCase()}`}
                >
                  <item.icon className="w-5 h-5 shrink-0" />
                  <span className="flex-1 truncate">{item.label}</span>
                </NavLink>
                {item.path === AGENT_GROUP_ANCHOR && <AgentNavGroup />}
              </Fragment>
          ))}
          {isSuperAdmin && isSuperAdminView && (
            <>
              <p className="px-3 pt-2 pb-1 text-xs font-semibold uppercase tracking-wider text-slate-400">
                Platform
              </p>
              {superAdminNavItems.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  end
                  className={({ isActive }) =>
                    cn(
                      'flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-md transition-colors',
                      isActive
                        ? 'bg-violet-50 dark:bg-violet-900/20 text-violet-700 dark:text-violet-400'
                        : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-white'
                    )
                  }
                  data-testid={`nav-super-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <item.icon className="w-5 h-5 shrink-0" />
                  <span className="flex-1 truncate">{item.label}</span>
                </NavLink>
              ))}
            </>
          )}
          </nav>
        </ScrollArea>

        {!isSuperAdminView && currentOrg?.id && (
          <SidebarPlanUsage organizationId={currentOrg.id} />
        )}

        <div className="shrink-0 border-t border-slate-200 bg-slate-50 px-3 py-2.5 dark:border-slate-800 dark:bg-slate-950">
          <a
            href="https://doc-tree.com"
            target="_blank"
            rel="noopener noreferrer"
            className="block text-center text-[11px] leading-snug text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200 transition-colors"
            data-testid="sidebar-powered-by"
          >
            Powered by <span className="font-semibold text-slate-700 dark:text-slate-300">DocTree</span>
          </a>
        </div>
      </div>

      {/* Footer */}
      <div className="border-t border-slate-200 dark:border-slate-800 p-3 space-y-3">
        {/* Theme Toggle */}
        <Button
          variant="ghost"
          className="w-full justify-start text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white"
          onClick={toggleTheme}
          data-testid="sidebar-theme-toggle"
        >
          {theme === 'dark' ? (
            <>
              <Sun className="w-5 h-5 mr-3" />
              Light Mode
            </>
          ) : (
            <>
              <Moon className="w-5 h-5 mr-3" />
              Dark Mode
            </>
          )}
        </Button>

        {/* User Menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              className="w-full justify-start text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white"
              data-testid="user-menu"
            >
              {user?.avatar_url ? (
                <img 
                  src={user.avatar_url} 
                  alt={user.name} 
                  className="w-8 h-8 rounded-full mr-3 object-cover"
                />
              ) : (
                <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center mr-3">
                  <span className="text-sm font-medium text-white">
                    {user?.name?.charAt(0).toUpperCase() || 'U'}
                  </span>
                </div>
              )}
              <div className="flex-1 text-left truncate">
                <div className="text-sm font-medium text-slate-900 dark:text-white truncate">
                  {user?.name}
                </div>
                <div className="text-xs text-slate-500 truncate">{user?.email}</div>
              </div>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-56" align="end" side="top">
            <DropdownMenuLabel>My Account</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {!isSuperAdminView && (
              <DropdownMenuItem onClick={() => navigate('/dashboard/profile')} data-testid="profile-menu-item">
                <User className="w-4 h-4 mr-2" />
                Profile Settings
              </DropdownMenuItem>
            )}
            {!isSuperAdminView && <DropdownMenuSeparator />}
            <DropdownMenuItem onClick={handleLogout} data-testid="logout-button">
              <LogOut className="w-4 h-4 mr-2" />
              Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {!isSuperAdminView && (
        <SearchModal isOpen={showSearch} onClose={() => setShowSearch(false)} />
      )}
    </aside>
  );
};

export default Sidebar;
